import { z } from "zod";

/**
 * US-E2.F3-0514-S01 (@UC0027 @UC9500) — kết quả QUÉT ẢNH danh sách học sinh → rows {tên, ngày sinh}.
 *
 * ═══ ĐÍCH ĐẾN, và vì sao KHÔNG tái dùng `ImportRowInput` ═══
 * Rows này đi vào `POST /v1/groups/:id/members` (`createGroupMemberSeatsRequestSchema` ở `groups.ts`)
 * để tạo **suất đại diện** — `GroupMember` chưa gắn user, vd học sinh trong nhóm phụ huynh.
 * KHÔNG dùng `member-import.ts`: `ImportRowInput` là `{name, email?, phone?}` — **không có chỗ chứa
 * ngày sinh**, và luồng đó đổ vào invite engine (gửi email mời), trong khi luồng này **không mời ai**.
 *
 * ═══ HAI FIELD CHO NGÀY SINH, KHÔNG PHẢI MỘT ═══
 * `birthDate` = thứ máy **dám suy**; `birthDateRaw` = thứ máy **đọc thấy**. Gộp lại thì hai trạng thái
 * rất khác nhau trở nên không phân biệt được ở chỗ đọc:
 *   · không đọc thấy gì            → `birthDate: null`, `birthDateRaw: null`
 *   · đọc thấy "05/06/2016" nhưng cả ngày lẫn tháng đều ≤ 12 nên không dám suy
 *                                  → `birthDate: null`, `birthDateRaw: "05/06/2016"`
 * Ca thứ hai là ca **thường gặp nhất**, không phải ca lỗi — và người sửa cần chuỗi thô để khỏi phải
 * mở lại ảnh.
 */

/** Trần số dòng một lần quét — khớp `createGroupMemberSeatsRequestSchema.members.max(100)`. */
export const MEMBER_OCR_ROWS_MAX = 100;

/** Trần độ dài tên — khớp `createGroupMemberSeatSchema.displayName.max(120)`. */
export const MEMBER_OCR_NAME_MAX_LENGTH = 120;

/** Trần chuỗi ngày thô đọc từ ảnh (chặn input phình to; đủ cho mọi định dạng ngày thực tế). */
export const MEMBER_OCR_RAW_DATE_MAX_LENGTH = 40;

/**
 * Mã cảnh báo theo từng dòng — **enum ĐÓNG**, không phải chuỗi tự do.
 *
 * Backend trả **mã**, client map sang câu (§7: *"backend trả `code` + `params`, không câu đã dịch"*).
 *
 * 🔴 `resolved_by_document_order` **KHÔNG phải cảnh báo lỗi.** Nó nói: *"ngày này mơ hồ, server giải
 * được nhờ bằng chứng ở dòng khác trong cùng ảnh"* — đúng ở mức **suy luận**, không ở mức **đọc trực
 * tiếp**. Màn duyệt phải tô riêng những dòng đó để người liếc qua; gộp chúng với dòng thường là bỏ
 * mất một mức tin cậy có thật.
 */
export const MEMBER_OCR_WARNINGS = [
  /** Cả ngày lẫn tháng đều ≤ 12 và ảnh không có bằng chứng định dạng ⇒ để trống, không đoán. */
  "ambiguous_day_month",
  /** Năm 2 chữ số — bằng chứng định dạng không cứu được thế kỷ. */
  "two_digit_year",
  /** Mọi cách đọc đều không ra ngày lịch có thật (vd 31/02). */
  "invalid_date",
  /** Ngày sinh ở tương lai. */
  "future_date",
  /** Không đọc thấy chuỗi ngày nào. */
  "missing_date",
  /** OCR trả độ tin cậy thấp cho dòng này. */
  "low_confidence",
  /** Tên trông không giống tên người (quá ngắn, toàn số, chứa `@`) — vẫn trả về, KHÔNG tự xoá. */
  "name_suspicious",
  /** Ngày mơ hồ nhưng GIẢI ĐƯỢC nhờ định dạng suy từ chính ảnh. Không phải lỗi — xem docblock. */
  "resolved_by_document_order",
  /**
   * @UC0340 (QĐ người dùng 18/10/2026) — tên đọc ra TRÙNG (`personNameMatchKey`, giữ dấu) với một suất ĐANG ở nhóm (đang dùng · giữ chỗ
   * · chờ duyệt). Chỉ là nhãn: người dùng quyết giữ hay xoá — đồng hương / đội bóng trùng tên là chuyện thường, máy KHÔNG tự loại.
   */
  "already_in_group",
] as const;

/**
 * @UC0340 — các mã CHỈ nói về ngày sinh. Nhóm THƯỜNG chỉ lấy TÊN (QĐ người dùng 18/10/2026: không đọc/lưu ngày sinh người lớn) ⇒ server bỏ
 * `birthDate`/`birthDateRaw` VÀ đúng các mã này khỏi kết quả quét. Tập ĐÓNG có tên — không lọc bằng chuỗi rải ở nơi gọi.
 */
export const MEMBER_OCR_DATE_WARNINGS = [
  "ambiguous_day_month",
  "two_digit_year",
  "invalid_date",
  "future_date",
  "missing_date",
  "resolved_by_document_order",
] as const satisfies readonly (typeof MEMBER_OCR_WARNINGS)[number][];

export type MemberOcrWarning = (typeof MEMBER_OCR_WARNINGS)[number];
export const memberOcrWarningSchema = z.enum(MEMBER_OCR_WARNINGS);

/**
 * Một dòng máy đọc được. `.strict()` — response này **chưa chạm DB**, nên mang `memberId`/`groupId`
 * vào đây là gợi ý sai rằng đã có gì đó được tạo.
 */
export const memberOcrScanRowSchema = z
  .object({
    displayName: z.string().trim().min(1).max(MEMBER_OCR_NAME_MAX_LENGTH),
    /** `YYYY-MM-DD` — NGÀY TRẦN, cùng luật với đích ghi. `null` = máy KHÔNG dám suy. */
    birthDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullable(),
    /** Chuỗi ngày THÔ đọc từ ảnh, giữ nguyên để người đối chiếu. `null` = không đọc thấy gì. */
    birthDateRaw: z.string().max(MEMBER_OCR_RAW_DATE_MAX_LENGTH).nullable(),
    warnings: z.array(memberOcrWarningSchema),
  })
  .strict();

export type MemberOcrScanRow = z.infer<typeof memberOcrScanRowSchema>;

/**
 * Envelope của `POST /v1/groups/:id/members/ocr-scan`.
 *
 * 🔴 `rows: []` là **kết quả hợp lệ** (*"ảnh này không có ai"*), KHÔNG phải lỗi — nhưng client phải
 * nói rõ bằng câu riêng, đừng để màn trống trơn.
 * 🔴 `truncated` tách khỏi `rows`: ảnh 130 dòng cắt còn 100 mà không nói là **giấu mất 30 người**.
 */
export const memberOcrScanResponseSchema = z
  .object({
    rows: z.array(memberOcrScanRowSchema).max(MEMBER_OCR_ROWS_MAX),
    truncated: z.boolean(),
  })
  .strict();

export type MemberOcrScanResponse = z.infer<typeof memberOcrScanResponseSchema>;
